import React, { useRef, useState } from 'react';
import apiService from '../../services/ApiService';

interface Props {
    onClose: () => void;
    onCreated: () => void;
    editGroup?: any;
}

export default function PortfolioGroupForm({ onClose, onCreated, editGroup }: Props) {
    const [name, setName] = useState(editGroup?.name || '');
    const [description, setDescription] = useState(editGroup?.description || '');
    const [discountRate, setDiscountRate] = useState(
        editGroup?.discount_rate != null ? parseFloat((editGroup.discount_rate * 100).toFixed(10)).toString() : '8'
    );
    const [saving, setSaving] = useState(false);
    const mouseDownOnBackdrop = useRef(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) return;

        setSaving(true);
        try {
            const data = {
                name: name.trim(),
                description: description.trim() || null,
                discount_rate: parseFloat(discountRate) / 100,
            };

            if (editGroup) {
                await apiService.updatePortfolioGroup(editGroup.id, data);
            } else {
                await apiService.createPortfolioGroup(data);
            }
            onCreated();
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onMouseDown={(e) => { mouseDownOnBackdrop.current = e.target === e.currentTarget; }}
            onClick={(e) => {
                if (e.target === e.currentTarget && mouseDownOnBackdrop.current) onClose();
                mouseDownOnBackdrop.current = false;
            }}
        >
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
                <div className="p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">
                        {editGroup ? 'Edit Portfolio Group' : 'New Portfolio Group'}
                    </h3>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="e.g., Fund I, Co-invest Vehicle"
                                autoFocus
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                rows={2}
                                placeholder="Optional description"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Target Discount Rate (%)</label>
                            <input
                                type="number"
                                value={discountRate}
                                onChange={(e) => setDiscountRate(e.target.value)}
                                step="0.1"
                                min="0"
                                max="100"
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={!name.trim() || saving}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                            >
                                {saving ? 'Saving...' : editGroup ? 'Save' : 'Create'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
